import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';

interface MenuItem {
  title: string;
  icon?: SafeHtml;
  pages: SidebarPage[];
}

interface SidebarPage {
  title: string;
  route?: string;
  children?: SidebarPage[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Output() toggle = new EventEmitter<any>();
  openIndex: number | null = null;
  isSidebarCollapsed = false;
  nestedOpenPages: Record<string, boolean> = {};
  private destroy$ = new Subject<void>();

  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private authService = inject(AuthService);

  protected get orgName(): string {
    return this.authService.getOrganizationName() || 'Enterprise AI';
  }

  sidebarMenu: MenuItem[] = [
    {
      title: 'Recruitment',
      icon: this.sanitizer.bypassSecurityTrustHtml(`
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6.97139 6.05561C7.32287 6.05561 7.67091 5.99022 7.99563 5.86319C8.32036 5.73616 8.61541 5.54996 8.86394 5.31523C9.11248 5.0805 9.30962 4.80184 9.44413 4.49515C9.57863 4.18846 9.64786 3.85976 9.64786 3.5278C9.64786 3.19585 9.57863 2.86714 9.44413 2.56045C9.30962 2.25377 9.11248 1.9751 8.86394 1.74038C8.61541 1.50565 8.32036 1.31945 7.99563 1.19242C7.67091 1.06538 7.32287 1 6.97139 1C6.26155 1 5.58078 1.26632 5.07884 1.74038C4.57691 2.21443 4.29492 2.85739 4.29492 3.5278C4.29492 4.19822 4.57691 4.84118 5.07884 5.31523C5.58078 5.78929 6.26155 6.05561 6.97139 6.05561Z" stroke="#1C1C1C" stroke-width="1.5" stroke-linejoin="round"/>
            <path d="M10.0294 8.94434H6.89412C5.18118 8.94434 4.32471 8.94434 3.67012 9.25923C3.09459 9.53619 2.62667 9.97812 2.33341 10.5217C2 11.1399 2 11.9488 2 13.5666V13.9999H10.0294M15 13.6388L13.7872 12.4937M13.7872 12.4937C14.074 12.2229 14.2352 11.8552 14.2353 11.4721C14.2354 11.138 14.1128 10.8141 13.8883 10.5557C13.6639 10.2973 13.3516 10.1204 13.0046 10.0552C12.6575 9.98992 12.2973 10.0403 11.9852 10.1978C11.6731 10.3553 11.4285 10.6101 11.293 10.9188C11.1575 11.2275 11.1396 11.5711 11.2422 11.8909C11.3449 12.2107 11.5618 12.487 11.856 12.6727C12.1501 12.8584 12.5034 12.9421 12.8555 12.9094C13.2077 12.8767 13.5369 12.73 13.7872 12.4937Z" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `),
      pages: [
        { title: 'Recruitment Dashboard', route: '/recruitment/dashboard' },
        { title: 'Active Job Postings', route: '/recruitment/jobs' },
        { title: 'Archived Jobs', route: '/recruitment/archived-jobs' },
        { title: 'Candidates List', route: '/recruitment/candidates' },
        { title: 'Add Job', route: '/recruitment/add-job' },
        { title: 'AI Assistant', route: '/recruitment/assistant' },
        { title: 'Approval Requests', route: '/recruitment/budget-approvals' },
      ],
    },
    {
      title: 'Onboarding',
      icon: this.sanitizer.bypassSecurityTrustHtml(`
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M1.69336 2.47461C1.69336 1.97733 1.89014 1.50041 2.24041 1.14878C2.59068 0.797153 3.06575 0.599609 3.56111 0.599609H7.84388C8.21527 0.599938 8.57133 0.748311 8.83379 1.01211L12.4899 4.68148L12.5525 4.74805C12.3751 4.85927 12.2106 4.99008 12.0622 5.13805L10.9789 6.22461H8.69744C8.32592 6.22461 7.96962 6.07645 7.70691 5.81273C7.44421 5.54901 7.29662 5.19132 7.29662 4.81836V1.53711H3.56111C3.31343 1.53711 3.0759 1.63588 2.90076 1.8117C2.72563 1.98751 2.62724 2.22597 2.62724 2.47461V13.7246C2.62724 13.9732 2.72563 14.2117 2.90076 14.3875C3.0759 14.5633 3.31343 14.6621 3.56111 14.6621H8.02878C8.0898 14.6652 8.15143 14.6652 8.21369 14.6621H11.0321C11.2798 14.6621 11.5173 14.5633 11.6925 14.3875C11.8676 14.2117 11.966 13.9732 11.966 13.7246V12.8452L12.8999 11.9077V13.7246C12.8999 14.2219 12.7031 14.6988 12.3528 15.0504C12.0026 15.4021 11.5275 15.5996 11.0321 15.5996H3.56111C3.06575 15.5996 2.59068 15.4021 2.24041 15.0504C1.89014 14.6988 1.69336 14.2219 1.69336 13.7246V2.47461ZM8.69744 5.28711H11.7727L8.2305 1.73117V4.81836C8.2305 4.94268 8.2797 5.06191 8.36726 5.14982C8.45483 5.23772 8.5736 5.28711 8.69744 5.28711ZM12.7225 5.80086L8.21182 10.329C7.94905 10.5929 7.7625 10.9233 7.67204 11.2852L7.32184 12.6896C7.3139 12.7218 7.30767 12.7543 7.30316 12.7871H4.02805C3.90421 12.7871 3.78545 12.8365 3.69788 12.9244C3.61031 13.0123 3.56111 13.1315 3.56111 13.2559C3.56111 13.3802 3.61031 13.4994 3.69788 13.5873C3.78545 13.6752 3.90421 13.7246 4.02805 13.7246H8.07174C8.10658 13.7266 8.1415 13.7266 8.17634 13.7246H8.2305C8.30957 13.7246 8.38179 13.7068 8.44716 13.6712L9.72844 13.3496C10.0889 13.2588 10.4181 13.0715 10.681 12.8077L15.1916 8.27961C15.5149 7.94991 15.6952 7.50532 15.6933 7.04272C15.6915 6.58012 15.5075 6.13701 15.1816 5.80996C14.8557 5.48291 14.4143 5.29843 13.9534 5.29672C13.4926 5.29502 13.0498 5.47623 12.7215 5.80086" fill="#1C1C1C"/>
          </svg>
        `),
      pages: [
        { title: 'Onboarding Dashboard', route: '/onboarding/dashboard' },
        { title: 'Templates', route: '/onboarding/templates' },
        { title: 'Active Onboarding List', route: '/onboarding/active-list' },
      ],
    },
    {
      title: 'Offboarding',
      icon: this.sanitizer.bypassSecurityTrustHtml(`
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clip-path="url(#clip0_4191_22853)">
              <path d="M11.1105 1.98654H13.4438C13.8563 1.98654 14.2519 2.14493 14.5436 2.42686C14.8353 2.7088 14.9992 3.09119 14.9992 3.4899V4.24159M11.1105 14.0135H13.4438C13.8563 14.0135 14.2519 13.8551 14.5436 13.5731C14.8353 13.2912 14.9992 12.9088 14.9992 12.5101V11.7584M11.1113 8H15M15 8L13.4445 6.49663M15 8L13.4445 9.50337M2.10827 13.5835L6.77468 14.9365C7.00698 15.0039 7.25236 15.0179 7.49123 14.9773C7.7301 14.9367 7.95584 14.8427 8.15042 14.7027C8.345 14.5628 8.50303 14.3808 8.61188 14.1714C8.72074 13.9619 8.7774 13.7307 8.77735 13.4963V2.5037C8.7774 2.26928 8.72074 2.03811 8.61188 1.82863C8.50303 1.61916 8.345 1.43719 8.15042 1.29725C7.95584 1.15732 7.7301 1.0633 7.49123 1.02271C7.25236 0.98212 7.00698 0.996078 6.77468 1.06347L2.10827 2.4165C1.78786 2.50946 1.50699 2.69978 1.30735 2.95922C1.10771 3.21866 0.999927 3.5334 1 3.85672V12.1433C0.999927 12.4666 1.10771 12.7813 1.30735 13.0408C1.50699 13.3002 1.78786 13.4905 2.10827 13.5835Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_4191_22853">
                <rect width="16" height="16" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        `),
      pages: [
        { title: 'Offboarding Dashboard', route: '/offboarding/offboarding-database' },
        { title: 'Exit Requests', route: '/offboarding/exit-requests' },
        { title: 'Asset Management', route: '/offboarding/asset-management' },
        { title: 'Knowledge Transfer', route: '/offboarding/knowledge-transfer' },
        { title: 'Clearance Control', route: '/offboarding/clearance-control' },
        { title: 'Final Payroll Management', route: '/offboarding/final-payroll-management' },
        { title: 'Closure Hub', route: '/offboarding/exit-documents' },
        { title: 'Templates', route: '/offboarding/templates' },
      ],
    },
    // {
    //   title: 'Programs',
    //   icon: this.sanitizer.bypassSecurityTrustHtml(`
    //       <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    //         <path d="M3.77273 8.5H10.8636M3.77273 5.54545H10.8636M12.0455 5.54545H13.2273M12.0455 8.5H13.2273M12.0455 11.4545H13.2273M3.77273 11.4545H10.8636M2 2H15V15H2V2Z" stroke="#1C1C1C" stroke-width="1.5" stroke-linejoin="round"/>
    //       </svg>
    //     `),
    //   pages: [
    //     { title: 'List 1', route: '/onboarding/list1' },
    //     { title: 'Lis 2', route: '/onboarding/list2' },
    //   ],
    // },
    {
      title: 'Vendor Evaluation',
      icon: this.sanitizer.bypassSecurityTrustHtml(`
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clip-path="url(#clip0_4191_22869_eval)">
              <path d="M5.33268 14.6663C5.70087 14.6663 5.99935 14.3679 5.99935 13.9997C5.99935 13.6315 5.70087 13.333 5.33268 13.333C4.96449 13.333 4.66602 13.6315 4.66602 13.9997C4.66602 14.3679 4.96449 14.6663 5.33268 14.6663Z" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.6667 14.6663C13.0349 14.6663 13.3333 14.3679 13.3333 13.9997C13.3333 13.6315 13.0349 13.333 12.6667 13.333C12.2985 13.333 12 13.6315 12 13.9997C12 14.3679 12.2985 14.6663 12.6667 14.6663Z" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1.36719 1.36719H2.70052L4.47385 9.64719C4.53891 9.95043 4.70763 10.2215 4.951 10.4138C5.19436 10.606 5.49712 10.7074 5.80719 10.7005H12.3272C12.6306 10.7 12.9248 10.596 13.1612 10.4057C13.3976 10.2154 13.5619 9.95021 13.6272 9.65385L14.7272 4.70052H3.41385" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_4191_22869_eval">
                <rect width="16" height="16" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        `),
      pages: [
        { title: 'Vendor Evaluation', route: '/vendor-evaluation' },
      ],
    },
    {
      title: 'Vendors',
      icon: this.sanitizer.bypassSecurityTrustHtml(`
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clip-path="url(#clip0_4191_22869)">
              <path d="M5.33268 14.6663C5.70087 14.6663 5.99935 14.3679 5.99935 13.9997C5.99935 13.6315 5.70087 13.333 5.33268 13.333C4.96449 13.333 4.66602 13.6315 4.66602 13.9997C4.66602 14.3679 4.96449 14.6663 5.33268 14.6663Z" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.6667 14.6663C13.0349 14.6663 13.3333 14.3679 13.3333 13.9997C13.3333 13.6315 13.0349 13.333 12.6667 13.333C12.2985 13.333 12 13.6315 12 13.9997C12 14.3679 12.2985 14.6663 12.6667 14.6663Z" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1.36719 1.36719H2.70052L4.47385 9.64719C4.53891 9.95043 4.70763 10.2215 4.951 10.4138C5.19436 10.606 5.49712 10.7074 5.80719 10.7005H12.3272C12.6306 10.7 12.9248 10.596 13.1612 10.4057C13.3976 10.2154 13.5619 9.95021 13.6272 9.65385L14.7272 4.70052H3.41385" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_4191_22869">
                <rect width="16" height="16" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        `),
      pages: [
        { title: 'Vendors Dashboard', route: '/vendors/dashboard' },
        { title: 'Vendors List', route: '/vendors/list' },
        { title: 'Process Template', route: '/vendors/process-templates' },
        { title: 'AI Assistant', route: '/vendors/assistant' },
      ],
    },
      {
        title: 'Vendor Finance',
        icon: this.sanitizer.bypassSecurityTrustHtml(`
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 12.6667H14" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M3.33398 10L5.73398 7.6L8.06732 9.26667L12.6673 4.66667" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.666 4.66667H12.666V6.66667" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `),
        pages: [
          { title: 'Vendor Finance Dashboard', route: '/vendor-finance/dashboard' },
          { title: 'Create PO', route: '/vendor-finance/create-po' },
          { title: 'Vendor Finance Register', route: '/vendor-finance' },
          { title: 'Purchase Order & Invoice Hub', route: '/vendor-finance/purchase-order-invoice-hub' },
          { title: 'Vendor Payments', route: '/vendor-finance/vendor-payments' },
          { title: 'Reporting', route: '/vendor-finance/reporting' },
        ],
      },

    {
      title: 'Employee',
      icon: this.sanitizer.bypassSecurityTrustHtml(`
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clip-path="url(#clip0_4191_22869)">
              <path d="M5.33268 14.6663C5.70087 14.6663 5.99935 14.3679 5.99935 13.9997C5.99935 13.6315 5.70087 13.333 5.33268 13.333C4.96449 13.333 4.66602 13.6315 4.66602 13.9997C4.66602 14.3679 4.96449 14.6663 5.33268 14.6663Z" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.6667 14.6663C13.0349 14.6663 13.3333 14.3679 13.3333 13.9997C13.3333 13.6315 13.0349 13.333 12.6667 13.333C12.2985 13.333 12 13.6315 12 13.9997C12 14.3679 12.2985 14.6663 12.6667 14.6663Z" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1.36719 1.36719H2.70052L4.47385 9.64719C4.53891 9.95043 4.70763 10.2215 4.951 10.4138C5.19436 10.606 5.49712 10.7074 5.80719 10.7005H12.3272C12.6306 10.7 12.9248 10.596 13.1612 10.4057C13.3976 10.2154 13.5619 9.95021 13.6272 9.65385L14.7272 4.70052H3.41385" stroke="#1C1C1C" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_4191_22869">
                <rect width="16" height="16" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        `),
      pages: [
        { title: 'Dashboard', route: '/employee/' },
        { title: 'Employee List', route: '/employee/list' },
        { title: 'Leave Overview', route: '/employee/attendance-leave-overview' },
      ],
    },
    {
      title: 'L and D',
      icon: this.sanitizer.bypassSecurityTrustHtml(`
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 2H10V4H6V2Z" fill="#1C1C1C"/>
            <path d="M3 5H13V13H3V5Z" stroke="#1C1C1C" stroke-width="1.5" stroke-linejoin="round"/>
            <path d="M5 7V11" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M8 7V11" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M11 7V11" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        `),
      pages: [
        { title: 'Dashboard', route: '/employee/l-and-d' },
        { title: 'Progress Tracking', route: '/employee/progress-tracking' },
        { title: 'Learning Records', route: '/employee/employee-learning-records' },
        { title: 'Skill Gap Analysis', route: '/employee/skill-gap-analysis' },
        { title: 'Courses & Content', route: '/employee/courses' },
        { title: 'Learning Paths', route: '/employee/learning-paths' },
        { title: 'Assessment & Evaluation', route: '/employee/assessment-and-evaluation' },
        { title: 'LD AI Assistance', route: '/employee/learning-assistant' },
      ],
    },
  ];

  ngOnInit() {
    this.syncOpenMenuToCurrentRoute();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.syncOpenMenuToCurrentRoute();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown(index: number) {
    this.openIndex = this.openIndex === index ? null : index;
    if (this.isSidebarCollapsed) {
      this.toggleSidebar();
    }
  }
  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.toggle.emit(this.isSidebarCollapsed);
  }

  onPageClick(item: MenuItem, page: SidebarPage) {
    if (page.children?.length) {
      this.toggleNestedPage(item.title, page.title);
    }

    if (page.route) {
      this.router.navigate([page.route]);
    }
  }

  isNestedPageOpen(menuTitle: string, pageTitle: string) {
    return !!this.nestedOpenPages[this.getNestedPageKey(menuTitle, pageTitle)];
  }

  isMenuActive(item: MenuItem) {
    return item.pages.some((page) => this.isPageRouteActive(page));
  }

  isPageActive(page: SidebarPage) {
    return this.isPageRouteActive(page);
  }

  private toggleNestedPage(menuTitle: string, pageTitle: string) {
    const key = this.getNestedPageKey(menuTitle, pageTitle);
    this.nestedOpenPages[key] = !this.nestedOpenPages[key];
  }

  private syncOpenMenuToCurrentRoute() {
    const matchedIndex = this.sidebarMenu.findIndex((item) => this.isMenuActive(item));
    if (matchedIndex !== -1) {
      this.openIndex = matchedIndex;
    }
  }

  private isPageRouteActive(page: SidebarPage): boolean {
    if (page.route) {
      const requiresExactMatch = page.route.endsWith('/') && page.route !== '/';
      const normalizedRoute = page.route.endsWith('/') && page.route !== '/' ? page.route.slice(0, -1) : page.route;
      const currentUrl = this.router.url.endsWith('/') && this.router.url !== '/' ? this.router.url.slice(0, -1) : this.router.url;

      if (currentUrl === normalizedRoute) {
        return true;
      }

      if (!requiresExactMatch && currentUrl.startsWith(`${normalizedRoute}/`)) {
        return true;
      }
    }

    return !!page.children?.some((childPage) => this.isPageRouteActive(childPage));
  }

  private getNestedPageKey(menuTitle: string, pageTitle: string) {
    return `${menuTitle}::${pageTitle}`;
  }

  logout() {
    this.authService.logout();
  }
}
